import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { getDhOrders, getDhStats } from '../../services/api';
import {
  Truck, Package, CheckCircle, Clock, XCircle, RefreshCw,
  ChevronLeft, ChevronRight, Send, AlertTriangle
} from 'lucide-react';

const STATUS_LABELS = {
  received: { label: 'Recibido', color: 'bg-blue-100 text-blue-700' },
  sent_to_pos: { label: 'Enviado a POS', color: 'bg-indigo-100 text-indigo-700' },
  ack_by_pos: { label: 'Confirmado', color: 'bg-purple-100 text-purple-700' },
  preparing: { label: 'Preparando', color: 'bg-yellow-100 text-yellow-700' },
  ready: { label: 'Listo', color: 'bg-emerald-100 text-emerald-700' },
  picked_up: { label: 'Recogido', color: 'bg-teal-100 text-teal-700' },
  in_transit: { label: 'En tránsito', color: 'bg-cyan-100 text-cyan-700' },
  delivered: { label: 'Entregado', color: 'bg-green-100 text-green-700' },
  completed: { label: 'Completado', color: 'bg-green-100 text-green-800' },
  failed: { label: 'Fallido', color: 'bg-red-100 text-red-700' },
  rejected: { label: 'Rechazado', color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelado', color: 'bg-slate-100 text-slate-600' },
};

const CHANNEL_LABELS = {
  mock: 'Mock', marketplace: 'Marketplace', menu_digital: 'Menú Digital',
  uber_eats: 'Uber Eats', rappi: 'Rappi', didi_food: 'DiDi Food',
};

const DeliveryHubAdmin = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (statusFilter) params.status = statusFilter;
      if (channelFilter) params.channel = channelFilter;
      const [ordersRes, statsRes] = await Promise.all([
        getDhOrders(params), getDhStats()
      ]);
      setOrders(ordersRes.data?.orders || []);
      setPagination(ordersRes.data?.pagination);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page, statusFilter, channelFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Truck className="w-5 h-5 text-indigo-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Delivery Hub — Admin</h1>
              <p className="text-sm text-slate-500">Todos los pedidos de plataformas externas</p>
            </div>
          </div>
          <button onClick={loadData} className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700">
            <RefreshCw className="w-4 h-4" /> Actualizar
          </button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{stats?.total_orders || 0}</p>
            <p className="text-xs text-slate-500 mt-1">Total pedidos</p>
          </div>
          {stats?.by_status && Object.entries(stats.by_status).slice(0, 3).map(([s, count]) => (
            <div key={s} className="bg-white rounded-xl border border-slate-200 p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{count}</p>
              <p className="text-xs text-slate-500 mt-1">{STATUS_LABELS[s]?.label || s}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([k, { label }]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <select value={channelFilter} onChange={(e) => { setChannelFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
            <option value="">Todos los canales</option>
            {Object.entries(CHANNEL_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>

        {/* Orders table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Pedido</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Negocio</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Canal</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Cliente</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Total</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan="7" className="px-4 py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" /></td></tr>
                ) : orders.length === 0 ? (
                  <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-500">No hay pedidos</td></tr>
                ) : orders.map((o) => {
                  const si = STATUS_LABELS[o.status] || { label: o.status, color: 'bg-slate-100 text-slate-600' };
                  const c = o.customer_info || {};
                  const d = o.created_at ? new Date(o.created_at).toLocaleString('es-MX', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—';
                  return (
                    <tr key={o.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <span className="text-sm font-mono font-medium text-slate-900">{o.external_order_id || o.id?.slice(0,8)}</span>
                        {o.is_overage && <span className="text-xs text-red-600 flex items-center gap-0.5 mt-0.5"><AlertTriangle className="w-3 h-3" /> Excedente</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{o.tenant_id?.slice(0,8)}...</td>
                      <td className="px-4 py-3"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{CHANNEL_LABELS[o.channel] || o.channel}</span></td>
                      <td className="px-4 py-3 text-sm text-slate-900">{c.name || 'Cliente'}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-slate-900">${(o.total||0).toLocaleString('es-MX',{minimumFractionDigits:2})}</td>
                      <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${si.color}`}>{si.label}</span></td>
                      <td className="px-4 py-3 text-sm text-slate-500">{d}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pagination && pagination.pages > 1 && (
            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
              <span className="text-sm text-slate-500">{pagination.total} pedidos</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1} className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-sm text-slate-600">{page} / {pagination.pages}</span>
                <button onClick={() => setPage(p => Math.min(pagination.pages,p+1))} disabled={page===pagination.pages} className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DeliveryHubAdmin;
